import {gql} from '@apollo/client';
import React from 'react';

import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import ProcessRealtimeUpdateSubscriptionWrapper from 'processes/ProcessRealtimeUpdateSubscriptionWrapper';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

import {
  ProcessFlowchartStructureQuery,
  ProcessFlowchartStructureQueryVariables,
} from './__generated/ProcessFlowchartStructureQuery';
import {
  ProcessTaskGroupFlowchartDataQuery,
  ProcessTaskGroupFlowchartDataQueryVariables,
} from './__generated/ProcessTaskGroupFlowchartDataQuery';
import {PROCESS_FLOWCHART_STRUCTURE_QUERY} from './LiveUpdatingTaskFlowchartWrapper';

type ProcessFlowchartStructure = ExtractByTypename<
  ProcessFlowchartStructureQuery['processFlowcharts'][number],
  'ProcessFlowchartNode'
>;
export const FLOWCHART_PROCESS_DATA_FRAGMENT = gql`
  fragment FlowchartProcessDataInfo on ProcessNode {
    id
    nodeConfig {
      id
    }
    taskStatusCounts(rootTaskId: $rootTaskId) {
      count
      status {
        id
        displayName
        statusType
      }
    }
    hasTasksWithFailedMappings
    flowchartPredecessorNodes(rootTaskId: $rootTaskId) {
      id
    }
    flowchartNodeUrl(rootTaskId: $rootTaskId)
  }
`;

export const PROCESS_TASK_GROUP_FLOWCHART_DATA_QUERY = gql`
  query ProcessTaskGroupFlowchartDataQuery($processId: StrictString!, $rootTaskId: StrictString)
  @skipAudit
  @engComponent(name: WORKFLOWS) {
    process(id: $processId) {
      ... on ProcessNode {
        ...FlowchartProcessDataInfo
        childProcesses {
          ... on ProcessNode {
            ...FlowchartProcessDataInfo
          }
          ...InaccessibleInfoGraphQL
        }
        isMappingInProgressForFlowchartRootedAtProcess(rootTaskId: $rootTaskId)
      }

      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
  ${FLOWCHART_PROCESS_DATA_FRAGMENT}
`;

export function NonPollingTaskGroupFlowchartWrapper(props: {
  flowchartIds: Array<string>;
  processId: string;
  rootTaskId?: string;
  refetchParent: () => Promise<unknown>;
  children: (
    flowcharts: Array<ProcessFlowchartStructure>,
    flowchartData: ProcessTaskGroupFlowchartDataQuery,
    forceRefetch: () => void
  ) => JSX.Element | null;
}): JSX.Element | null {
  const flowchartStructureResults = useBenchlingQuery<
    ProcessFlowchartStructureQuery,
    ProcessFlowchartStructureQueryVariables
  >(PROCESS_FLOWCHART_STRUCTURE_QUERY, {
    renderCachedDataWhenRefetching: true,
    variables: {flowchartIds: props.flowchartIds},
  });

  const flowchartDataResults = useBenchlingQuery<
    ProcessTaskGroupFlowchartDataQuery,
    ProcessTaskGroupFlowchartDataQueryVariables
  >(PROCESS_TASK_GROUP_FLOWCHART_DATA_QUERY, {
    variables: {processId: props.processId, rootTaskId: props.rootTaskId},
    renderCachedDataWhenRefetching: true,
    fetchPolicy: 'network-only',
  });

  if (
    flowchartStructureResults.type !== QueryResultType.SUCCESS ||
    flowchartDataResults.type !== QueryResultType.SUCCESS
  ) {
    return <CenteredHorizontalThrobber />;
  }

  const flowcharts = flowchartStructureResults.data.processFlowcharts.filter(
    (flowchart): flowchart is ProcessFlowchartStructure => !isInaccessible(flowchart)
  );

  return props.children(flowcharts, flowchartDataResults.data, () => {
    props.refetchParent();
    flowchartStructureResults.refetch();
    flowchartDataResults.refetch();
  });
}

export interface LiveUpdatingTaskGroupFlowchartWrapperProps {
  parentId: string;
  flowchartIds: Array<string>;
  processId: string;
  rootTaskId?: string;
  children: (
    flowcharts: Array<ProcessFlowchartStructure>,
    flowchartData: ProcessTaskGroupFlowchartDataQuery,
    forceRefetch: () => void
  ) => JSX.Element | null;
  refetch: () => Promise<unknown>;
}

export default function LiveUpdatingTaskGroupFlowchartWrapper(
  props: LiveUpdatingTaskGroupFlowchartWrapperProps
): JSX.Element {
  return (
    <NonPollingTaskGroupFlowchartWrapper
      flowchartIds={props.flowchartIds}
      processId={props.processId}
      rootTaskId={props.rootTaskId}
      refetchParent={props.refetch}
    >
      {(flowcharts, flowchartData, forceRefetch) => {
        let hasIncompleteData = false;
        if (!isInaccessible(flowchartData.process)) {
          hasIncompleteData = flowchartData.process.isMappingInProgressForFlowchartRootedAtProcess;
        }

        return (
          <ProcessRealtimeUpdateSubscriptionWrapper
            apiId={props.parentId}
            messageFilter="no_filter"
            refetch={forceRefetch}
            refetchOnMessagesFromSameClient={true}
            hasIncompleteDataShouldRefetch={hasIncompleteData}
          >
            {props.children(flowcharts, flowchartData, forceRefetch)}
          </ProcessRealtimeUpdateSubscriptionWrapper>
        );
      }}
    </NonPollingTaskGroupFlowchartWrapper>
  );
}
